import React from "react";
import { Link } from "react-router-dom";
import { FiUsers } from "react-icons/fi";
import { PanelCard } from "./dashboardShared";
import { UserAvatar } from "../UserAvatar";
import { RoleBadgeList, type RoleBadgeInfo } from "../RoleBadges";
import { useRealtime, type PresenceUser } from "../../Realtime/RealtimeProvider";
import { useUserDirectory } from "../Account/useUserDirectory";
import { usePermissions } from "../../Auth/PermissionsContext";

const MAX_SHOWN = 8;

/**
 * Carte « Équipe » du dashboard (à droite d'« Accès preview ») : les personnes CONNECTÉES en ce
 * moment (même source temps réel que la pile d'avatars de la barre du haut), chacune avec ses
 * badges de rôles maximaux (dominance serveur). La liste administrative complète vit sur /users.
 */
export const TeamCard: React.FC = () => {
	const { connected, users } = useRealtime();
	const { resolveBadges } = useUserDirectory();
	const { badges: ownBadges, isMe } = usePermissions();

	const online = users.slice(0, MAX_SHOWN);
	const overflow = Math.max(0, users.length - MAX_SHOWN);

	return (
		<PanelCard
			title="Équipe"
			subtitle={connected ? `${users.length} en ligne` : "Connexion au temps réel…"}
			action={<FiUsers className="h-4 w-4 text-slate-400" aria-hidden />}
			bodyClassName="py-1"
		>
			{online.length === 0 ? (
				<p className="px-5 py-6 text-sm text-slate-400">Personne n'est en ligne pour le moment.</p>
			) : (
				<ul className="divide-y divide-slate-100">
					{online.map((u: PresenceUser, i) => {
						// Le TAG de permission de chaque personne connectée : l'annuaire d'abord ; pour sa
						// propre session (absente de l'annuaire en local), repli sur ses badges de contexte.
						let badges = (resolveBadges(u.email ?? u.user) as RoleBadgeInfo[]) ?? [];
						if (badges.length === 0 && (isMe(u.email) || isMe(u.user))) badges = (ownBadges as RoleBadgeInfo[]) ?? [];
						const row = (
							<>
								<span className="relative shrink-0">
									<UserAvatar name={u.user} id={u.userId ?? undefined} avatarKey={u.avatarKey} pictureUrl={u.pictureUrl} />
									<span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" aria-hidden />
								</span>
								<span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-700">
									{u.user}
									{u.pages?.length ? <span className="ml-1.5 text-xs font-normal text-slate-400">{u.pages.length} page{u.pages.length > 1 ? "s" : ""} ouverte{u.pages.length > 1 ? "s" : ""}</span> : null}
								</span>
								<RoleBadgeList badges={badges} max={2} className="shrink-0" />
							</>
						);
						return (
							<li key={`${u.userId ?? u.user}-${i}`}>
								{u.userId ? (
									<Link to={`/account/${u.userId}`} className="flex items-center gap-3 px-5 py-2 transition hover:bg-slate-50" aria-label={`Profil de ${u.user}`}>
										{row}
									</Link>
								) : (
									<span className="flex items-center gap-3 px-5 py-2">{row}</span>
								)}
							</li>
						);
					})}
					{overflow > 0 && (
						<li className="px-5 py-2 text-xs text-slate-400">
							et {overflow} autre{overflow > 1 ? "s" : ""} personne{overflow > 1 ? "s" : ""} en ligne
						</li>
					)}
				</ul>
			)}
		</PanelCard>
	);
};

export default TeamCard;
